import { isReservedSlug } from './reserved-slugs.js';

/**
 * Which tenant a URL belongs to, read from its path (ADR-0039).
 *
 * The white-label app is served at `kilatcarwash.net/{slug}`, so the first path
 * segment names the operator. This is the one place that decides what counts as
 * a tenant path, and both sides use it: the Cloud Functions that serve the
 * per-tenant manifest, icons and branding CSS, and the client that decides which
 * tenant to boot as.
 *
 * Shared rather than implemented twice on purpose. If the server thinks
 * `/sparkle/branding.css` belongs to `sparkle` and the client thinks it belongs
 * to nobody, the app loads with no palette and no error — the silent-wrong-screen
 * failure this codebase keeps re-learning.
 *
 * Pure: no URL parsing beyond string work, no environment, no I/O.
 */

/** Locale prefixes the marketing router matches, which may precede a path. */
const LOCALE_PREFIXES: ReadonlySet<string> = new Set(['en', 'ms', 'zh', 'ta']);

/**
 * The tenant slug in a pathname, or `null` when the path is not a tenant's.
 *
 * Returns `null` for a reserved segment rather than the segment itself, so a
 * request for `/pricing` can never be interpreted as a tenant called "pricing"
 * — which would mean a marketing page trying to resolve branding, and failing.
 *
 * Accepts a full pathname (`/sparkle/book`), a bare slug (`sparkle`), and either
 * with or without leading and trailing slashes, because callers hand it
 * `location.pathname`, an Express `req.path`, and hand-written values, and a
 * resolver that is fussy about slashes fails in exactly one of the three.
 */
export function tenantSlugFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  let first = segments[0];
  if (first === undefined) return null;

  // A localised tenant link (`/ms/sparkle/book`) names the tenant second. Only
  // step over the locale when something follows it — `/ms` on its own is the
  // Malay marketing site, not a tenant with an empty name.
  if (LOCALE_PREFIXES.has(first.toLowerCase()) && segments.length >= 2) {
    first = segments[1];
    if (first === undefined) return null;
  }

  const candidate = first.toLowerCase();

  // A segment with a dot is a file — `/branding.css`, `/favicon.ico` — not a
  // tenant. Checked before the reserved list so a new asset at the root does
  // not have to be added to that list to stop being read as an operator.
  if (candidate.includes('.')) return null;

  if (isReservedSlug(candidate)) return null;

  // Same shape the allocator guarantees, so anything that could never have been
  // issued is rejected here rather than becoming a lookup that always misses.
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(candidate)) return null;
  if (candidate.length < 3 || candidate.length > 40) return null;

  return candidate;
}

/**
 * The path a tenant's app is rooted at, with both slashes.
 *
 * Used as the router basename, the service-worker scope, and the manifest
 * `scope`/`start_url`. Those three must agree exactly — a service worker whose
 * scope is `/sparkle` while the router thinks it is `/sparkle/` controls a
 * different set of URLs than the app it was shipped with.
 */
export function tenantBasePath(slug: string): string {
  return `/${slug}/`;
}

/**
 * Strip the tenant prefix, leaving the route the app should render.
 *
 * `/sparkle/book` → `/book`, `/sparkle` → `/`. Returns the path unchanged when
 * it does not belong to a tenant, so a caller can pass anything.
 */
export function stripTenantPrefix(pathname: string): string {
  const slug = tenantSlugFromPath(pathname);
  if (!slug) return pathname;

  const segments = pathname.split('/').filter(Boolean);
  const index = segments.indexOf(slug);
  if (index === -1) return pathname;

  const rest = segments.slice(index + 1).join('/');
  return rest ? `/${rest}` : '/';
}

/**
 * The public URL of a tenant's white-label app.
 *
 * THE single place this URL is constructed. Before 2026-08-21 six different
 * call sites built it by hand as `https://{slug}.{customerSubdomainSuffix}`,
 * and when ADR-0039 moved delivery to a path under the marketing domain, every
 * one of them kept emitting the old shape. They were all still "working" —
 * producing a well-formed URL that resolved to nothing:
 *
 *   - the "Open in app" button on every /find listing and detail page
 *   - the server-rendered link in each outlet's SEO/OG meta
 *   - the URL preview shown to a new operator during onboarding
 *   - the URL super-admin sees when creating a tenant
 *   - and the PRINTED QR CODE, which an operator laminates and puts on the
 *     shop wall, where a dead link cannot be hot-fixed
 *
 * `customerSubdomainSuffix` still exists on the brand and is still correct for
 * matching an INBOUND host — legacy subdomains and Phase-2 custom domains — but
 * it must never be used to build an outbound URL again.
 */
export function customerAppUrl(domains: { readonly marketing: string }, slug: string): string {
  return `https://${domains.marketing}${tenantBasePath(slug)}`;
}

/**
 * A tenant's app URL for printing or reading aloud.
 *
 * Same target without the scheme, for a QR caption, a poster, or a support
 * call. `kilatcarwash.net/sparkle` is the whole point of path delivery — short
 * enough to say out loud, which a subdomain never was.
 */
export function customerAppUrlForDisplay(
  domains: { readonly marketing: string },
  slug: string,
): string {
  return `${domains.marketing}${tenantBasePath(slug)}`;
}
